/* landing-records.js — hydrate the "How They Made It" records + sidebar rail.
 *
 * The rail is built FROM the records on the page, never a hardcoded list: every
 * option and count is tallied off the DOM, so it can't claim a tool or a total
 * that isn't there, and a new story with new tags appears with no code change.
 *
 * Records render fully server-side and read fine with JS off; this only adds the
 * facet lists, the spec rows and the filtering.
 *
 * Idempotent on purpose. This theme re-runs init — default.hbs says so outright
 * ("idempotent, so double-running is safe") — and an earlier version appended
 * without clearing, which shipped every chip and filter option twice.
 */
(function () {
  'use strict';
  var list = document.getElementById('recList');
  var rail = document.getElementById('recRail');
  if (!list || !rail) return;

  var section = list.closest('.records') || list;
  if (section.dataset.hydrated === '1') return;
  section.dataset.hydrated = '1';

  var countEl = document.getElementById('recCount');
  var emptyEl = document.getElementById('recEmpty');
  var resetBtn = document.getElementById('recReset');
  var recs = [].slice.call(list.querySelectorAll('.rec'));

  // How many options each facet is allowed in the sidebar. A 29-item vertical
  // list isn't a filter, it's a wall — the long tail stays on the records.
  var CAP = { medium: 8, tool: 6, method: 4 };

  // A stack should read like a pipeline, not an inventory. Alphabetical order put
  // "After Effects" first on a record whose actual generator is Midjourney, and
  // the bolded first item claims to be the primary tool — so order by role:
  // image/video/audio generators, then upscale/lipsync, then edit and finish.
  // PAIRED WITH THE VAULT: the same list lives as TOOLS in the pipeline repo at
  // `2 Pipeline/_automation/lib/creator-facets.js`, which is what actually writes
  // the #tool: tags at publish time. Separate repos, so they can't import each
  // other — add a new tool to BOTH. Drift is cosmetic, not breaking: a tool
  // missing here still renders, it just sorts last. `node
  // .config/audit-creator-story-facets.js --vocab` lists what's in use and
  // flags anything this array hasn't got.
  var ORDER = [
    'Midjourney', 'Stable Diffusion', 'Flux', 'DALL-E', 'Seedream', 'Ideogram', 'Leonardo', 'Firefly',
    'Nano Banana', 'Nano Banana Pro', 'Krea', 'Freepik', 'OpenArt', 'ComfyUI',
    // Kinovi is a multi-model HOST, not a model — it runs Seedance/Kling/etc.
    // underneath. Ranked immediately before Seedance so a record carrying both
    // reads "Kinovi → Seedance": the platform the artist credits, then the
    // model actually doing the work.
    'Runway', 'Kling', 'Veo', 'Sora', 'Luma', 'Pika', 'Hailuo', 'Kinovi', 'Seedance', 'Wan', 'LTX',
    'Vidu', 'Pixverse', 'Higgsfield', 'Deforum',
    'Suno', 'Udio', 'Ableton', 'ElevenLabs', 'Hedra', 'HeyGen', 'Viggle',
    'Magnific', 'Topaz', 'Descript', 'ChatGPT',
    'Photoshop', 'Procreate', 'Blender', 'Cinema 4D', 'Unreal', 'Nuke',
    'After Effects', 'Premiere', 'DaVinci Resolve', 'CapCut'
  ];
  var MAX_TOOLS = 4;   // the mockup shows a short stack; the rest would be noise

  function rank(t) { var i = ORDER.indexOf(t); return i === -1 ? ORDER.length : i; }

  // "#tool:Midjourney" -> {k:'tool', v:'Midjourney'}. Values live in the tag NAME
  // so casing and characters (e.g. "@daejin.creates") survive intact.
  function parse(el) {
    var out = { medium: null, method: null, handle: null, tool: [] };
    (el.getAttribute('data-tax') || '').split('|').forEach(function (raw) {
      var m = raw.match(/^#(medium|method|handle|tool):(.+)$/);
      if (!m) return;
      var v = m[2].trim();
      if (m[1] === 'tool') out.tool.push(v);
      else out[m[1]] = v;
    });
    out.tool.sort(function (a, b) { return rank(a) - rank(b) || a.localeCompare(b); });
    return out;
  }

  var data = recs.map(function (el) {
    var t = parse(el);
    el.__tax = t;

    var set = function (slot, text) {
      var n = el.querySelector('[data-slot="' + slot + '"]');
      if (n) n.textContent = text || '';
      return n;
    };
    set('medium', t.medium);
    set('handle', t.handle);

    if (t.tool.length) {
      // Arrow-separated text, first tool accented — not chips.
      var v = el.querySelector('[data-slot="tools"]');
      v.textContent = '';
      t.tool.slice(0, MAX_TOOLS).forEach(function (name, i) {
        if (i) {
          var sep = document.createElement('span');
          sep.className = 'sep';
          sep.textContent = '→';
          v.appendChild(sep);
        }
        var node = document.createElement(i ? 'span' : 'b');
        node.textContent = name;
        v.appendChild(node);
      });
      if (t.tool.length > MAX_TOOLS) {
        var more = document.createElement('span');
        more.className = 'more';
        more.textContent = ' +' + (t.tool.length - MAX_TOOLS);
        more.title = t.tool.join(', ');
        v.appendChild(more);
      }
      el.querySelector('[data-slot="tools-row"]').hidden = false;
    }
    if (t.method) {
      set('works', t.method);
      el.querySelector('[data-slot="works-row"]').hidden = false;
    }
    return t;
  });

  // ── build the rail from what's actually present ──
  var sel = { medium: null, tool: null, method: null };

  function tally(key) {
    var m = Object.create(null);
    data.forEach(function (t) {
      var vals = key === 'tool' ? t.tool : t[key] ? [t[key]] : [];
      vals.forEach(function (v) { m[v] = (m[v] || 0) + 1; });
    });
    return Object.keys(m)
      .map(function (v) { return { v: v, n: m[v] }; })
      .sort(function (a, b) { return b.n - a.n || a.v.localeCompare(b.v); });
  }

  [].slice.call(rail.querySelectorAll('.rail-grp')).forEach(function (grp) {
    var facet = grp.getAttribute('data-facet');
    var ul = grp.querySelector('ul');
    ul.textContent = '';
    var opts = tally(facet).slice(0, CAP[facet] || 6);
    if (!opts.length) { grp.hidden = true; return; }
    opts.forEach(function (o) {
      var li = document.createElement('li');
      var b = document.createElement('button');
      b.type = 'button';
      b.setAttribute('aria-pressed', 'false');
      var label = document.createElement('span');
      label.textContent = o.v;
      var n = document.createElement('span');
      n.className = 'n';
      n.textContent = o.n;
      b.appendChild(label);
      b.appendChild(n);
      b.addEventListener('click', function () {
        sel[facet] = sel[facet] === o.v ? null : o.v;
        apply();
      });
      b.__facet = facet; b.__val = o.v;
      li.appendChild(b);
      ul.appendChild(li);
    });
  });

  function apply() {
    var shown = 0;
    recs.forEach(function (el) {
      var t = el.__tax;
      var ok = (!sel.medium || t.medium === sel.medium) &&
               (!sel.method || t.method === sel.method) &&
               (!sel.tool || t.tool.indexOf(sel.tool) !== -1);
      el.hidden = !ok;
      if (ok) shown++;
    });
    [].slice.call(rail.querySelectorAll('button')).forEach(function (b) {
      if (b.__facet) b.setAttribute('aria-pressed', sel[b.__facet] === b.__val ? 'true' : 'false');
    });
    var filtered = sel.medium || sel.tool || sel.method;
    countEl.textContent = filtered
      ? shown + ' of ' + recs.length + ' stories'
      : recs.length + ' stories · newest first';
    if (emptyEl) emptyEl.hidden = shown !== 0;
  }

  if (resetBtn) resetBtn.addEventListener('click', function () {
    sel.medium = sel.tool = sel.method = null;
    apply();
  });
  apply();
})();
